import React, { useState, useCallback, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { SX_DESTAQUE, useRolarAteDestaque } from '@/hooks/useRolarAteDestaque'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Grid,
  Stepper,
  Step,
  StepLabel,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Breadcrumbs,
  Link,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import DownloadIcon from '@mui/icons-material/Download'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { Oportunidade, OportunidadeEstagio } from '@/types/oportunidade'
import { Produto, Parceiro } from '@/types/produto'
import { formatCurrency, formatDate } from '@/utils/formatters'
import { oportunidadeService } from '@/services/oportunidadeService'
import { produtoService, parceiroService } from '@/services/produtoService'
import ComissoesDaReserva from '@/components/comissao/ComissoesDaReserva'
import ParceirosCompativeisCard from '@/components/parceiros/ParceirosCompativeisCard'
import EncaminhamentoParceiroCard from '@/components/parceiros/EncaminhamentoParceiroCard'
import ScoreAderenciaCard from '@/components/oportunidades/ScoreAderenciaCard'
import ScoreComercialCard from '@/components/oportunidades/ScoreComercialCard'
import HistoricoEtapasCard from '@/components/oportunidades/HistoricoEtapasCard'
import { useMotivosPerda } from '@/components/pipeline/useMotivosPerda'
import { authStore } from '@/store/authStore'
import { EtapaComercial, ETAPA_COMERCIAL_LABEL } from '@/types/statusReserva'
import { papelPrincipal } from '@/components/layout/menu'
import ReclassificacaoProdutoCard from '@/components/reservas/ReclassificacaoProdutoCard'
import { ROTULO_OUTRA_NECESSIDADE, reservaEhOutraNecessidade } from '@/utils/outraNecessidade'
import { CODIGO_OUTRA_NECESSIDADE } from '@/constants/codigosProduto'
import SecaoElegibilidadeReserva from '@/components/reservas/SecaoElegibilidadeReserva'

/** As cinco etapas da linha do funil — "Perdido" é desfecho, não passo. */
const ETAPAS_LINHA: EtapaComercial[] = ['RESERVA', 'REUNIAO', 'ESTRUTURACAO', 'NEGOCIACAO', 'GANHO']

const estagioLabelMap: Record<OportunidadeEstagio, string> = {
  DIAGNOSTICO: 'Diagnóstico',
  ESTRUTURACAO: 'Estruturação',
  NEGOCIACAO: 'Negociação',
  CONTRATADA: 'Contratada',
}

const OportunidadeDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  // Lançar comissão é ato do back-office; o consultor acompanha pelo Financeiro.
  const usuarioLogado = authStore((st) => st.usuario)
  const podeOperar = !!usuarioLogado?.permissoes?.some((perm) =>
    ['ADMIN', 'GERENTE', 'OPERADOR'].includes(perm.toUpperCase())
  )
  // P1 (AN-PP1 §7.1, Q-PP1-2): reclassificar "Outra necessidade" é do back-office SEM escopo de
  // dono — mesma persona do menu: CONSULTOR com OPERADOR residual continua consultor.
  const papel = papelPrincipal(usuarioLogado?.permissoes)
  const podeReclassificar = papel === 'ADMIN' || papel === 'GERENTE' || papel === 'OPERADOR'
  // Seleção formal de parceiro é ADMIN (Handoff v4.0) — espelha o @PreAuthorize do endpoint.
  const podeSelecionarParceiro = !!usuarioLogado?.permissoes?.some(
    (perm) => perm.toUpperCase() === 'ADMIN'
  )
  const navigate = useNavigate()

  const selecionarParceiro = async (parceiroId: string, nome: string) => {
    if (!id || !usuarioLogado?.id) return
    try {
      const atualizada = await oportunidadeService.selecionarParceiro(id, {
        parceiroId,
        parceiroNome: nome,
        usuarioId: usuarioLogado.id,
      })
      setOportunidade((prev) => (prev ? { ...prev, ...atualizada } : atualizada))
    } catch (e) {
      // O card mostra o estado; o erro chega pelo snackbar global do interceptor. Aqui só
      // garantimos que a promessa resolve para o botão sair do estado "Selecionando…".
      console.error('Falha ao selecionar parceiro', e)
    }
  }

  const [oportunidade, setOportunidade] = useState<Oportunidade | null>(null)
  const [produtos, setProdutos] = useState<Produto[]>([])
  const [parceiros, setParceiros] = useState<Parceiro[]>([])
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  // Deep link da Central (L7 / Q40, `?foco=proxima-acao`): a próxima ação se resolve aqui —
  // destaca o card e rola até ele. A reserva vem do GET escopado: de outro consultor, erro.
  const [searchParams] = useSearchParams()
  const focoProximaAcao = searchParams.get('foco') === 'proxima-acao'
  useRolarAteDestaque(focoProximaAcao && oportunidade ? 'proxima-acao' : null, !loading)

  const [editMode, setEditMode] = useState(false)
  // Rascunho local só enquanto edita; a verdade é `oportunidade.proximaAcao` (persistida —
  // Fase 6, item 18). Antes este estado ERA a verdade e sumia ao sair da tela.
  const [proximaAcao, setProximaAcao] = useState('')
  const [salvandoAcao, setSalvandoAcao] = useState(false)
  const [erroAcao, setErroAcao] = useState<string | null>(null)
  const [propostaDialogOpen, setPropostaDialogOpen] = useState(false)
  /** P2: incrementa quando a elegibilidade muda fora da seção (sobreposição no Encaminhamento). */
  const [versaoElegibilidade, setVersaoElegibilidade] = useState(0)

  // Motivo padronizado da perda (8.6): o catálogo só é carregado quando há o que traduzir.
  const perdida = oportunidade?.etapaComercial === 'PERDIDO'
  const { rotuloDe: rotuloMotivo } = useMotivosPerda(perdida)

  useEffect(() => {
    if (!id) return
    let ativo = true
    setLoading(true)
    setErro(null)
    Promise.all([
      oportunidadeService.buscar(id),
      // "Recomendados" = primeiros produtos do catálogo (não há endpoint de
      // recomendação dedicado). Parceiros vêm da lista completa.
      produtoService.listar(0, 3).then((p) => p.content ?? []).catch(() => [] as Produto[]),
      parceiroService.listar().catch(() => [] as Parceiro[]),
    ])
      .then(([opu, produtosLista, parceirosLista]) => {
        if (!ativo) return
        setOportunidade(opu)
        setProdutos(produtosLista as Produto[])
        setParceiros(parceirosLista as Parceiro[])
      })
      .catch((e) => {
        if (ativo) setErro(e instanceof Error ? e.message : 'Falha ao carregar oportunidade')
      })
      .finally(() => {
        if (ativo) setLoading(false)
      })
    return () => { ativo = false }
  }, [id])

  const iniciarEdicaoAcao = useCallback(() => {
    setProximaAcao(oportunidade?.proximaAcao ?? '')
    setErroAcao(null)
    setEditMode(true)
  }, [oportunidade?.proximaAcao])

  const handleSaveChanges = useCallback(async () => {
    if (!id) return
    setSalvandoAcao(true)
    setErroAcao(null)
    try {
      const atualizada = await oportunidadeService.definirProximaAcao(id, proximaAcao)
      setOportunidade((prev) => (prev ? { ...prev, ...atualizada } : atualizada))
      setEditMode(false)
    } catch (e) {
      setErroAcao(e instanceof Error && e.message ? e.message : 'Não foi possível salvar a próxima ação.')
    } finally {
      setSalvandoAcao(false)
    }
  }, [id, proximaAcao])

  const handleGerarProposta = useCallback(() => {
    setPropostaDialogOpen(false)
  }, [])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (erro || !oportunidade) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro ?? 'Oportunidade não encontrada'}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/oportunidades')}>
          Voltar às Oportunidades
        </Button>
      </Box>
    )
  }

  // "Outra necessidade" (OTHER_NEED) não é produto a recomendar.
  const produtosRecomendados = produtos.filter((p) => p.codigo !== CODIGO_OUTRA_NECESSIDADE)
  const outraNecessidade = reservaEhOutraNecessidade(oportunidade)
  const parceirosDisponiveis = parceiros.filter((p) => p.ativo)

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/oportunidades')}
          sx={{ cursor: 'pointer' }}
        >
          Oportunidades
        </Link>
        <Typography color="textPrimary">{oportunidade.nomeCliente}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h4" fontWeight={600}>
              {oportunidade.tipoNecessidade}
            </Typography>
            {/* P1 (AN-PP1 §3.2): pelo código (ou legado sem produto), nunca pelo nome. */}
            {outraNecessidade && <Chip size="small" variant="outlined" label={ROTULO_OUTRA_NECESSIDADE} />}
          </Stack>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
            Cliente: {oportunidade.nomeCliente}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="success"
          onClick={() => setPropostaDialogOpen(true)}
        >
          Gerar Proposta
        </Button>
      </Stack>

      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={8}>
          {/* Card Principal */}
          <Card sx={{ mb: 3 }}>
            <CardHeader title="Informações da Oportunidade" />
            <CardContent>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Valor Estimado
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {formatCurrency(oportunidade.valorEstimado)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Estágio
                    </Typography>
                    <Chip
                      label={estagioLabelMap[oportunidade.estagio]}
                      color={
                        oportunidade.estagio === 'CONTRATADA'
                          ? 'success'
                          : 'warning'
                      }
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Descrição
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {oportunidade.descricao}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Etapa comercial (decisão 6.4): as seis do handoff, derivadas no backend.
              "Perdido" não é passo da linha — é desfecho; aparece como aviso no lugar do
              stepper. Backend anterior (sem etapaComercial) cai no stepper legado. */}
          <Card sx={{ mb: 3 }}>
            <CardHeader
              title="Etapa comercial"
              subheader={oportunidade.etapaComercial === 'REUNIAO'
                ? 'Primeiro contato registrado — em reunião com o cliente'
                : undefined}
            />
            <CardContent>
              {oportunidade.etapaComercial === 'PERDIDO' ? (
                <Alert severity="warning" icon={false} data-testid="alerta-perdida">
                  Reserva <strong>perdida</strong> — cancelada, não convertida ou reprovada. O histórico
                  e os status continuam disponíveis no back-office.
                  {/* Motivo padronizado (8.6) + observação, como o backend gravou. */}
                  {oportunidade.motivoPerdaCodigo && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Motivo da perda:</strong>{' '}
                      {rotuloMotivo(oportunidade.motivoPerdaCodigo) ?? oportunidade.motivoPerdaCodigo}
                      {oportunidade.motivoPerdaObservacao ? ` — ${oportunidade.motivoPerdaObservacao}` : ''}
                    </Typography>
                  )}
                </Alert>
              ) : oportunidade.etapaComercial ? (
                <Stepper activeStep={ETAPAS_LINHA.indexOf(oportunidade.etapaComercial)} alternativeLabel>
                  {ETAPAS_LINHA.map((e) => (
                    <Step key={e} completed={ETAPAS_LINHA.indexOf(e) < ETAPAS_LINHA.indexOf(oportunidade.etapaComercial!)}>
                      <StepLabel>{ETAPA_COMERCIAL_LABEL[e]}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              ) : (
                <Stepper activeStep={['DIAGNOSTICO', 'ESTRUTURACAO', 'NEGOCIACAO', 'CONTRATADA'].indexOf(oportunidade.estagio)}>
                  <Step><StepLabel>Diagnóstico</StepLabel></Step>
                  <Step><StepLabel>Estruturação</StepLabel></Step>
                  <Step><StepLabel>Negociação</StepLabel></Step>
                  <Step><StepLabel>Contratada</StepLabel></Step>
                </Stepper>
              )}
            </CardContent>
          </Card>

          {/* P2 (AN-PP2 §8): elegibilidade gravada pelo servidor (snapshot, rota, decisões).
              `key` remonta a seção depois de uma sobreposição feita no Encaminhamento. */}
          {id && (
            <SecaoElegibilidadeReserva
              key={versaoElegibilidade}
              oportunidadeId={id}
              papel={papel}
              modo="detalhe"
            />
          )}

          {/* P1 (AN-PP1 §7): "Outra necessidade" — reclassificação pelo back-office e histórico. */}
          <ReclassificacaoProdutoCard
            oportunidade={oportunidade}
            podeReclassificar={podeReclassificar}
            onReclassificada={(atualizada) =>
              setOportunidade((prev) => (prev ? { ...prev, ...atualizada } : atualizada))
            }
          />

          {/* Produtos Recomendados */}
          <Card sx={{ mb: 3 }}>
            <CardHeader title="Produtos Recomendados" />
            <CardContent>
              {produtosRecomendados.length === 0 && (
                <Typography variant="body2" color="textSecondary">
                  Nenhum produto disponível no catálogo.
                </Typography>
              )}
              <Grid container spacing={2}>
                {produtosRecomendados.map((produto) => (
                  <Grid item xs={12} key={produto.id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="start">
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {produto.nome}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {produto.categoria}
                            </Typography>
                            {/* P1 (AN-PP1 §6): taxa/valor do catálogo eram demonstrativos — saíram. */}
                            <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
                              {produto.descricao}
                            </Typography>
                          </Box>
                          {/* Simulação de comissão (decisão 8.2, 28/09): independe da flag
                              SIMULADOR_HABILITADO (que governa só o simulador de crédito antigo).
                              `?reserva=<id>`: a página de Simulações pré-seleciona esta reserva
                              no modo "Por reserva" (Rodada 3). */}
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => navigate(`/simulacoes?reserva=${encodeURIComponent(oportunidade.id)}`)}
                          >
                            Simular
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Histórico real de etapas (P10): `GET /{id}/historico-etapas` — substitui a lista
              fixa de dois itens (criação/última atualização) que não era histórico. */}
          {id && (
            <HistoricoEtapasCard
              oportunidadeId={id}
              rotuloMotivo={rotuloMotivo}
              usuarioAtualId={usuarioLogado?.id}
            />
          )}
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={4}>
          {/* Parceiro escolhido pela Originei — o ÚNICO dado de parceiro que o consultor
              vê (Handoff v4.0). A lista de compatibilidade, abaixo, é só do back-office. */}
          {oportunidade.parceiroSelecionadoNome && (
            <Card sx={{ mb: 3 }}>
              <CardHeader title="Parceiro da operação" />
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip color="primary" label={oportunidade.parceiroSelecionadoNome} />
                  {oportunidade.parceiroSelecionadoEm && (
                    <Typography variant="caption" color="textSecondary">
                      selecionado em {formatDate(oportunidade.parceiroSelecionadoEm)}
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Score Comercial (8.1) e Aderência (Q5) lado a lado, cada um com seu rótulo:
              o primeiro é derivado da etapa (vem no detalhe), o segundo diz o que falta. */}
          <ScoreComercialCard score={oportunidade.scoreComercial} />
          {id && <ScoreAderenciaCard oportunidadeId={id} />}

          {/* Comissão do consultor (Etapa 17) — lançamento manual pelo operador. */}
          {id && <ComissoesDaReserva oportunidadeId={id} podeLancar={podeOperar} />}

          {/*
            Motor de compatibilidade — só faz sentido com o vínculo formal de produto
            (reservas antigas, sem produtoId, não têm contra o que casar) e só para o
            back-office, que é quem direciona ao parceiro.
          */}
          {podeOperar && oportunidade.produtoId && (
            <ParceirosCompativeisCard
              produtoId={oportunidade.produtoId}
              valorEstimado={oportunidade.valorEstimado}
              parceiroSelecionadoId={oportunidade.parceiroSelecionadoId}
              onSelecionar={podeSelecionarParceiro ? selecionarParceiro : undefined}
            />
          )}

          {/* Encaminhamento formal (Épico 4) — back-office; só existe com o destinatário
              formal (parceiro selecionado). Encaminhar em si é ADMIN, como a seleção. */}
          {podeOperar && id && oportunidade.parceiroSelecionadoId && (
            <EncaminhamentoParceiroCard
              oportunidadeId={id}
              parceiroNome={oportunidade.parceiroSelecionadoNome}
              podeEncaminhar={podeSelecionarParceiro}
              // P2 (Q67): sobrepor impedimento de elegibilidade é ADMIN pela persona do menu.
              podeSobrepor={papel === 'ADMIN'}
              onElegibilidadeAlterada={() => setVersaoElegibilidade((v) => v + 1)}
            />
          )}

          {/* Próxima ação — persistida na reserva (Fase 6, item 18); aparece também no
              Kanban e na lista. Vazio limpa: "não há próxima ação" é estado legítimo. */}
          <Card
            id="proxima-acao"
            data-destacado={focoProximaAcao ? 'true' : undefined}
            sx={{ mb: 3, ...(focoProximaAcao ? SX_DESTAQUE : {}) }}
          >
            <CardHeader
              title="Próxima Ação"
              subheader={
                !editMode && oportunidade.proximaAcaoAtualizadaEm
                  ? `atualizada em ${formatDate(oportunidade.proximaAcaoAtualizadaEm)}`
                  : undefined
              }
              action={
                editMode ? (
                  <IconButton
                    size="small"
                    aria-label="Salvar próxima ação"
                    onClick={() => void handleSaveChanges()}
                    disabled={salvandoAcao}
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <IconButton size="small" aria-label="Editar próxima ação" onClick={iniciarEdicaoAcao}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                )
              }
            />
            <CardContent>
              {editMode ? (
                <Stack spacing={1}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={proximaAcao}
                    onChange={(e) => setProximaAcao(e.target.value)}
                    placeholder="O que fazer a seguir nesta reserva…"
                    size="small"
                    inputProps={{ maxLength: 500 }}
                    disabled={salvandoAcao}
                    helperText={`${proximaAcao.length}/500 · vazio limpa`}
                  />
                  {erroAcao && <Alert severity="error">{erroAcao}</Alert>}
                </Stack>
              ) : (
                <Typography
                  variant="body2"
                  color={oportunidade.proximaAcao ? 'textPrimary' : 'textSecondary'}
                  sx={{ whiteSpace: 'pre-wrap' }}
                >
                  {oportunidade.proximaAcao || 'Nenhuma ação definida'}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* O card "Probabilidade de Fechamento" (slider) saiu na decisão 6.3: era um
              número digitado que nascia 30 e nunca era recalibrado. O ScoreAderenciaCard,
              na coluna ao lado, é o substituto — calculado e explicável. */}

          {/* Card Documentos */}
          <Card sx={{ mb: 3 }}>
            <CardHeader title="Documentos" />
            <CardContent>
              <Stack spacing={1}>
                <Button variant="outlined" startIcon={<FileDownloadIcon />} fullWidth>
                  Upload de Documento
                </Button>
                <Typography variant="caption" color="textSecondary" sx={{ textAlign: 'center' }}>
                  Nenhum documento anexado
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Gerar Proposta Dialog */}
      <Dialog
        open={propostaDialogOpen}
        onClose={() => setPropostaDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Gerar Proposta</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <Typography variant="body2">
              Selecione o produto e parceiro para gerar a proposta:
            </Typography>
            <Box>
              <Typography variant="caption" fontWeight={600}>
                Produto: {produtosRecomendados[0]?.nome}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" fontWeight={600}>
                Parceiros Disponíveis:
              </Typography>
              <Stack spacing={1} sx={{ mt: 1 }}>
                {parceirosDisponiveis.length === 0 && (
                  <Typography variant="caption" color="textSecondary">
                    Nenhum parceiro ativo cadastrado.
                  </Typography>
                )}
                {parceirosDisponiveis.map((parceiro) => (
                  <Card key={parceiro.id} variant="outlined">
                    <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                      <Typography variant="body2" fontWeight={600}>
                        {parceiro.nome}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {parceiro.tipo} · {parceiro.cnpj}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPropostaDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleGerarProposta}
          >
            Gerar e Baixar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default OportunidadeDetailPage
